import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import Switch from '../../../../../../molecules/Switch'
import DataGridConfigEditor from './typeEditors/DataGridConfigEditor'
import PresentationTableConfigEditor from './typeEditors/PresentationTableConfigEditor'
import MetricConfigEditor from './typeEditors/MetricConfigEditor'
import ChartConfigEditor from './typeEditors/ChartConfigEditor'
import HierarchicalTableConfigEditor from './typeEditors/HierarchicalTableConfigEditor'
import HierarchicalGridConfigEditor from './typeEditors/HierarchicalGridConfigEditor'
import ScopeSelectorConfigEditor from './typeEditors/ScopeSelectorConfigEditor'
import HierarchicalPivotTableConfigEditor from './typeEditors/HierarchicalPivotTableConfigEditor'

const EDITOR_MAP = {
  'Data Grid': DataGridConfigEditor,
  'Presentation Table': PresentationTableConfigEditor,
  Metric: MetricConfigEditor,
  Chart: ChartConfigEditor,
  'Hierarchical Table': HierarchicalTableConfigEditor,
  'Hierarchical Grid': HierarchicalGridConfigEditor,
  'Scope Selector': ScopeSelectorConfigEditor,
  'Hierarchical Pivot Table': HierarchicalPivotTableConfigEditor
}

function PresentationDetailPanel ({
  optionCode,
  optionData,
  allPurposes,
  onUpdateConfig,
  onUpdateOption
}) {
  const handleConfigUpdate = useCallback((updatedConfig) => {
    onUpdateConfig(optionCode, updatedConfig)
  }, [onUpdateConfig, optionCode])

  const handleScopeToggle = useCallback((e) => {
    onUpdateOption(optionCode, 'scope', { enabled: e.target.checked })
  }, [onUpdateOption, optionCode])

  const scopeEnabled = optionData?.scope?.enabled ?? false

  const purposeCode = optionData?.config?.purposeCode ??
    optionData?.config?.hierarchy?.levels?.[0]?.purposeCode
  const purposeData = allPurposes?.[purposeCode]
  const supportedScopeTypes = useMemo(() => {
    return (purposeData?.scopePredicates || []).map(sp => sp.scopeType)
  }, [purposeData])

  if (!optionCode || !optionData) {
    return (
      <div style={{
        padding: '32px',
        textAlign: 'center',
        color: 'var(--text-color-subtle)',
        paddingLeft: '32px'
      }}
      >
        <p>Select a presentation option to view and edit details</p>
      </div>
    )
  }

  const Editor = EDITOR_MAP[optionData.type] || null

  return (
    <div style={{ paddingLeft: '24px', overflowY: 'auto' }}>
      <div style={{ marginBottom: '16px' }}>
        <h3 style={{ marginBottom: '4px' }}>{optionData.displayName}</h3>
        <div style={{ fontSize: '0.875rem', color: 'var(--text-color-subtle)' }}>
          {optionData.type} • {optionCode}
        </div>
        {optionData.description
          ? (
            <p style={{ marginTop: '8px', fontSize: '0.875rem', color: 'var(--text-color-subtle)' }}>
              {optionData.description}
            </p>
          )
          : null}
      </div>

      {Editor
        ? <Editor key={optionCode} config={optionData.config} allPurposes={allPurposes} onUpdate={handleConfigUpdate} />
        : null}

      {optionData.type !== 'Scope Selector' && (
        <div style={{ marginTop: '16px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <h4 style={{ margin: 0 }}>Scope Participation</h4>
            <Switch checked={scopeEnabled} onChange={handleScopeToggle} />
          </div>
          {scopeEnabled && supportedScopeTypes.length > 0 && (
            <p style={{ fontSize: '0.875rem', color: 'var(--text-color-subtle)', margin: 0 }}>
              Supported scope types: {supportedScopeTypes.join(', ')}
            </p>
          )}
          {scopeEnabled && supportedScopeTypes.length === 0 && (
            <p style={{ fontSize: '0.875rem', color: 'var(--color-warning-500)', margin: 0 }}>
              The selected purpose does not declare any scope predicates.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

PresentationDetailPanel.propTypes = {
  optionCode: PropTypes.string,
  optionData: PropTypes.object,
  allPurposes: PropTypes.object.isRequired,
  onUpdateConfig: PropTypes.func.isRequired,
  onUpdateOption: PropTypes.func.isRequired
}

export default PresentationDetailPanel
